/**
 * Per-leg edit rules for the order edit form.
 *
 * Historically the edit dialog locked *every* field on the whole form from
 * the parent order's rollup status: an order whose first stop was already
 * in transit (so the parent reads `in_transit`) refused edits to stop 2,
 * even though that stop hadn't started. Legs are the authoritative unit of
 * work, so lock decisions are made per leg from that leg's own
 * `driver_status`, falling back to the order status only when a leg has no
 * status of its own (single-leg orders, or a leg row that predates the
 * per-leg status columns).
 *
 * This module is pure so both the field gating (which props are disabled)
 * and the save-time validation read exactly the same rules — a field the
 * user could edit can never be rejected on save, and vice versa.
 */
import type { OrderFormData, LegData } from "@/components/orders/orderFormTypes";
import { resolveFormLegs } from "@/lib/order-form-legs";

export interface LegEditLocks {
  /** Tractor / operators / trailer / trailer type / trip type are frozen. */
  assetsLocked: boolean;
  /** Ready & delivery windows are frozen. */
  timesLocked: boolean;
  /** Origin & destination addresses are frozen. */
  locationsLocked: boolean;
  /** Human explanation, empty when nothing is locked. */
  reason: string;
}

export interface LegEditRuleContext {
  /** Rollup status of the parent order. Used only when a leg has no status. */
  orderStatus?: unknown;
  /** Admin performing a historical / billing-only correction. Unlocks
   *  terminal legs; every external side effect is already suppressed by the
   *  `_historicalEdit` flag on the write path. */
  historicalEdit?: boolean;
}

export interface LegRuleInput extends LegEditRuleContext {
  /** This leg's own `legs.driver_status`, when known. */
  legStatus?: unknown;
  /** True when `orderStatus` is a multi-leg rollup rather than one stop's own
   *  status. A parent flips to in_transit/delivered off ANY leg, so using it
   *  as a fallback would freeze stops that haven't started. Capped at
   *  `confirmed` (canceled still propagates — the whole order is dead). */
  parentIsRollup?: boolean;
}

const ROLLUP_CAP = new Set(["in_transit", "delivered", "tender_received"]);

function fallbackStatus(input: LegRuleInput): string {
  const parent = norm(input.orderStatus);
  if (!parent) return "";
  if (input.parentIsRollup && ROLLUP_CAP.has(parent)) return "confirmed";
  return parent;
}

const OPEN: LegEditLocks = {
  assetsLocked: false,
  timesLocked: false,
  locationsLocked: false,
  reason: "",
};

const norm = (v: unknown): string => String(v ?? "").trim().toLowerCase();

/** Decide what a single stop may still change, given its own status. */
export function planLegEditRules(input: LegRuleInput): LegEditLocks {
  const status = norm(input.legStatus) || fallbackStatus(input) || "booked";

  if (status === "delivered" || status === "canceled") {
    if (input.historicalEdit) return { ...OPEN };
    return {
      assetsLocked: true,
      timesLocked: true,
      locationsLocked: true,
      reason:
        status === "canceled"
          ? "This stop is canceled and can no longer be edited."
          : "This stop is delivered and can no longer be edited.",
    };
  }

  if (status === "in_transit") {
    return {
      assetsLocked: true,
      timesLocked: false,
      locationsLocked: true,
      reason: "This stop is in transit — its truck, trailer and route are locked.",
    };
  }

  if (status === "tender_received") {
    return {
      assetsLocked: true,
      timesLocked: false,
      locationsLocked: false,
      reason: "Assignments are locked until the tender is accepted.",
    };
  }

  // booked / confirmed / anything unknown → fully editable. Confirmed legs
  // stay editable on purpose: dispatch retimes and re-crews confirmed work
  // all day, and the write path re-syncs the external trip.
  return { ...OPEN };
}

/** Per-leg locks for a form, index-aligned with `resolveFormLegs(fd)`. */
export function resolveLegEditLocks(
  fd: OrderFormData,
  ctx: LegEditRuleContext = {},
): LegEditLocks[] {
  return resolveFormLegs(fd).map((stop) =>
    planLegEditRules({
      legStatus: (stop.leg as (LegData & { driver_status?: unknown }) | undefined)
        ?.driver_status,
      orderStatus: ctx.orderStatus ?? fd.status,
      parentIsRollup: fd.order_type === "multi-leg",
      historicalEdit: ctx.historicalEdit,
    }),
  );
}

const ASSET_FIELDS = [
  "assigned_tractor_id",
  "assigned_operator_id",
  "assigned_operator_2_id",
  "trailer_id",
  "trailer_type_id",
  "trip_type",
] as const;

const TIME_FIELDS = [
  "ready_time",
  "ready_time_end",
  "requested_delivery_time",
  "requested_delivery_time_end",
] as const;

const LOCATION_FIELDS = [
  "ship_from_location_id",
  "ship_from_street_address",
  "ship_from_city",
  "ship_from_province_state",
  "ship_from_postal_zip_code",
  "ship_from_country",
  "ship_to_location_id",
  "ship_to_street_address",
  "ship_to_city",
  "ship_to_province_state",
  "ship_to_postal_zip_code",
  "ship_to_country",
] as const;

type AnyRecord = Record<string, unknown>;

function changed(a: AnyRecord, b: AnyRecord, fields: readonly string[]): boolean {
  return fields.some((f) => norm(a[f]) !== norm(b[f]));
}

/**
 * Save-time guard: reject only the changes a *locked* leg made. Edits to a
 * sibling stop that is still open always pass, whatever the other stops (or
 * the parent rollup) say.
 *
 * Legs are matched by `id`; a leg with no counterpart in the original form
 * is new and has nothing to protect.
 */
export function validateLegEdits(
  next: OrderFormData,
  original: OrderFormData | undefined,
  ctx: LegEditRuleContext = {},
): string[] {
  if (!original) return [];
  const reasons: string[] = [];

  const isMulti = next.order_type === "multi-leg";
  if (!isMulti) {
    const locks = planLegEditRules({
      legStatus: (next.legs?.[0] as (LegData & { driver_status?: unknown }) | undefined)
        ?.driver_status,
      orderStatus: ctx.orderStatus ?? original.status,
      historicalEdit: ctx.historicalEdit,
    });
    const a = next as unknown as AnyRecord;
    const b = original as unknown as AnyRecord;
    if (locks.assetsLocked && changed(a, b, ASSET_FIELDS)) reasons.push(locks.reason);
    else if (locks.timesLocked && changed(a, b, TIME_FIELDS)) reasons.push(locks.reason);
    else if (locks.locationsLocked && changed(a, b, LOCATION_FIELDS))
      reasons.push(locks.reason);
    return reasons;
  }

  const origById = new Map<string, LegData>();
  (original.legs ?? []).forEach((l) => {
    if (l.id) origById.set(l.id, l);
  });

  (next.legs ?? []).forEach((leg, index) => {
    const prev = leg.id ? origById.get(leg.id) : undefined;
    if (!prev) return; // newly added stop
    const locks = planLegEditRules({
      legStatus: (prev as LegData & { driver_status?: unknown }).driver_status,
      orderStatus: ctx.orderStatus ?? original.status,
      parentIsRollup: true,
      historicalEdit: ctx.historicalEdit,
    });
    if (!locks.assetsLocked && !locks.timesLocked && !locks.locationsLocked) return;
    const a = leg as unknown as AnyRecord;
    const b = prev as unknown as AnyRecord;
    const p = `Stop ${index + 1}: `;
    if (locks.assetsLocked && changed(a, b, ASSET_FIELDS)) {
      reasons.push(`${p}${locks.reason}`);
    } else if (locks.timesLocked && changed(a, b, TIME_FIELDS)) {
      reasons.push(`${p}${locks.reason}`);
    } else if (locks.locationsLocked && changed(a, b, LOCATION_FIELDS)) {
      reasons.push(`${p}${locks.reason}`);
    }
  });

  return reasons;
}
